import { NOM_FORMULE, type Formule } from '@repere/core';
import { useRegime, type RegimeAcces } from './regime';

/**
 * La formule de repli, quand le serveur n'en renvoie pas.
 *
 * Une réponse d'API plus ancienne ne doit pas faire disparaître le badge : le compte a
 * toujours un régime, et le traduire en formule ici coûte quatre lignes. Le cas `couvert`
 * y suit la même règle que côté serveur — `solo`, jamais `duo`.
 */
const FORMULE_PAR_REGIME: Record<RegimeAcces, Formule> = {
  gratuit: 'link',
  abonne: 'solo',
  couvert: 'solo',
  professionnel: 'pro',
};

/**
 * Le nom de la formule, à côté de la marque.
 *
 * Il répond à une question qu'on ne devrait pas avoir à se poser et qui se posait
 * pourtant : « la messagerie fonctionne, suis-je en train de payer ? ». Le calendrier et
 * les alertes *sont* gratuits, mais rien à l'écran ne le disait, et découvrir son régime en
 * butant sur un refus est la pire façon de l'apprendre.
 *
 * Il porte désormais le nom public de la formule — **Link**, **Solo**, **Duo**, **Pro** —
 * et non plus une périphrase maison. « Version gratuite » et « version parent » décrivaient
 * un état sans dire ce qu'il y avait à côté ; les quatre noms de la grille tarifaire
 * s'annoncent, se comparent et se retrouvent d'un écran à l'autre.
 *
 * Deux choses n'ont pas changé, et ne doivent pas :
 *
 * - **Le badge ne dit jamais « Duo » à un compte couvert.** Il affiche `Solo`, exactement
 *   comme un abonné individuel. La règle vient du produit : le parent couvert voit que
 *   l'écriture est ouverte, pas à qui il le doit. C'est le serveur qui la tient
 *   (`DroitsView.formule`), pour qu'aucun écran ne puisse la relâcher par distraction.
 * - **Il ne s'affiche qu'une fois la réponse arrivée.** Un badge qui clignote de « Link »
 *   à « Solo » au démarrage donnerait deux réponses en une seconde.
 */
export function BadgeAcces() {
  const droits = useRegime();

  if (!droits.data) return null;

  const formule = droits.data.formule ?? FORMULE_PAR_REGIME[droits.data.acces];

  // La classe reste celle du régime, pas celle de la formule : c'est elle que la feuille de
  // style connaît (`.badge-acces.professionnel`), et une classe composée à la volée
  // échapperait au test qui vérifie que toute classe du balisage a bien une règle.
  return <span className={`badge-acces ${droits.data.acces}`}>{NOM_FORMULE[formule]}</span>;
}
